/**
 * One section on the rail: its header, then its rows. The whole block is
 * sortable among the top-level items, dragged by the header, so its rows travel
 * with it. The header doubles as a drop slot meaning "the top level, just above
 * this section"; the rows area is a drop target meaning "into this section".
 */
import React from 'react';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { idOf } from '@shared/navigation/railLayout';

import { RailEntryView } from './RailEntryView';
import { sectionMenuItems, type RailActions } from './railMenus';
import { SectionHeaderRow } from './SectionHeaderRow';

import type { RailNodeData } from './useRailDrag';
import type { NavCounts } from '@app/stores/slices/uiSlice';
import type { RailLayout, RailSection } from '@shared/navigation/railLayout';

/** Rename actions a section header adds to the shared rail actions. */
export type SectionActions = RailActions & {
  rename: (id: string, label: string) => void;
  cancelRename: () => void;
};

interface SectionBlockProps {
  section: RailSection;
  layout: RailLayout;
  isCollapsed: boolean;
  /** Edit mode: an empty section shows a visible *Drag rows here* box. */
  editMode: boolean;
  renaming: boolean;
  counts: NavCounts;
  rowClick: { onClick?: () => void };
  actions: SectionActions;
}

/** The section's rows, sortable among themselves and droppable as a whole. */
const SectionRows: React.FC<Omit<SectionBlockProps, 'renaming'>> = ({ section, ...ctx }) => {
  const data: RailNodeData = { kind: 'rows', container: section.id };
  const { setNodeRef } = useDroppable({ id: `rows:${section.id}`, data });
  return (
    <div ref={setNodeRef}>
      <SortableContext items={section.ids.map(idOf)} strategy={verticalListSortingStrategy}>
        {section.ids.map(entry => (
          <RailEntryView key={idOf(entry)} entry={entry} container={section.id} ctx={ctx} />
        ))}
      </SortableContext>
      {section.ids.length === 0 && ctx.editMode && !ctx.isCollapsed && (
        <div className="border-sidebar-border text-sidebar-foreground/50 mx-1 flex h-7 items-center rounded border border-dashed px-2 text-xs">
          Drag rows here
        </div>
      )}
    </div>
  );
};

/** The header, as a drop slot for "above this section". */
const HeaderSlot: React.FC<{ sectionId: string; children: React.ReactNode }> = ({
  sectionId,
  children,
}) => {
  const data: RailNodeData = { kind: 'slot', before: sectionId };
  const { setNodeRef } = useDroppable({ id: `slot:${sectionId}`, data });
  return <div ref={setNodeRef}>{children}</div>;
};

const SECTION_DATA: RailNodeData = { kind: 'section' };

/** A section block. Top and bottom margins give it room from its neighbours; the first and last sit flush. */
export const SectionBlock: React.FC<SectionBlockProps> = props => {
  const { section, layout, isCollapsed, editMode, renaming, actions } = props;
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: SECTION_DATA,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      role="group"
      aria-label={section.label}
      className="my-3 first:mt-0 last:mb-0"
    >
      {!isCollapsed && (
        <HeaderSlot sectionId={section.id}>
          <SectionHeaderRow
            label={section.label}
            editing={renaming}
            dragListeners={listeners}
            menuItems={sectionMenuItems(layout, section.id, { editMode, isCollapsed }, actions)}
            onStartRename={() => actions.startRename(section.id)}
            onRename={label => actions.rename(section.id, label)}
            onCancelRename={actions.cancelRename}
          />
        </HeaderSlot>
      )}
      <SectionRows {...props} />
    </div>
  );
};
